import { useEventLogging } from '@innovamat/event-logging';
import type { ReactNode } from 'react';
import { createContext, useContext, useState } from 'react';
import { TEST_TYPE, type TestRow } from '../../../../../modules/test/test';

type UseTestAndResultsValues = {
  drawerId: string | undefined;
  isNextDisabled: boolean;
  isPreviousDisabled: boolean;
  rowSelected: TestRow | undefined;
  testRowIds: string[];
  onClickNext: () => void;
  onClickPrevious: () => void;
  onCloseDrawer: () => void;
  onOpenDrawer: (id: string) => void;
};

type Props = {
  rows: TestRow[];
  children: ReactNode;
  classroomId: string;
};

const TestAndResultsContext = createContext<
  UseTestAndResultsValues | undefined
>(undefined);

function TestAndResultsProvider({
  rows,
  children,
  classroomId,
}: Props): JSX.Element {
  const { setEventData } = useEventLogging();
  const [drawerId, setDrawerId] = useState<string | undefined>(undefined);

  const testRows = rows.filter(
    (test) =>
      test.type === TEST_TYPE.MOCK ||
      test.type === TEST_TYPE.CONMAT ||
      test.type === TEST_TYPE.COSMOS
  );

  const testRowIds = testRows.map((test) => test.id);

  const onOpenDrawer = (id: string): void => {
    setDrawerId(id);
  };

  const onCloseDrawer = (): void => {
    setDrawerId(undefined);
  };

  const onClickNext = (): void => {
    if (!drawerId) {
      return;
    }

    const currentIndex = testRowIds.indexOf(drawerId);

    if (currentIndex >= 0 && currentIndex < testRowIds.length - 1) {
      const nextTestId = testRowIds[currentIndex + 1];
      const nextTest = testRows.find((test) => test.id === nextTestId);

      if (nextTest) {
        setEventData('test_report_next', {
          test_id: nextTest.testId,
          round_id: nextTest.classroomTestRoundId,
          class_id: classroomId,
        });
      }

      setDrawerId(nextTestId);
    }
  };

  const onClickPrevious = (): void => {
    if (!drawerId) {
      return;
    }

    const currentIndex = testRowIds.indexOf(drawerId);
    if (currentIndex > 0) {
      const prevTestId = testRowIds[currentIndex - 1];
      const prevTest = testRows.find((test) => test.id === prevTestId);

      if (prevTest) {
        setEventData('test_report_previous', {
          test_id: prevTest.testId,
          round_id: prevTest.classroomTestRoundId,
          class_id: classroomId,
        });
      }

      setDrawerId(prevTestId);
    }
  };

  const isNextDisabled =
    testRowIds.indexOf(drawerId ?? '') === testRowIds.length - 1;
  const isPreviousDisabled = testRowIds.indexOf(drawerId!) === 0;

  const rowSelected = testRows.find((test) => test.id === drawerId);

  return (
    <TestAndResultsContext.Provider
      value={{
        drawerId,
        isNextDisabled,
        isPreviousDisabled,
        rowSelected,
        testRowIds,
        onClickNext,
        onClickPrevious,
        onCloseDrawer,
        onOpenDrawer,
      }}
    >
      {children}
    </TestAndResultsContext.Provider>
  );
}

export const useTestAndResults = (): UseTestAndResultsValues => {
  const context = useContext(TestAndResultsContext);
  if (!context) {
    throw new Error(
      'useTestAndResults must be used within a TestAndResultsProvider'
    );
  }
  return context;
};

export { TestAndResultsProvider };
